// @flow
import React, {Component} from 'react';
import * as Table from 'reactabular-table';
import * as sort from 'sortabular';
import * as resolve from 'table-resolver';
import Paginator from '../Paginator/Paginator';
import orderBy from 'lodash/orderBy'; //eslint-disable-line lodash-fp/use-fp
import _ from 'lodash/fp';
import './AdminList.css';

const mapWithIndex = _.map.convert({cap: false});

const sortingOrder = {
  FIRST: 'asc',
  asc: 'desc',
  desc: 'asc',
};

const mapColumns = (sortable, columns) =>
  _.map(c => ({
    ...c,
    header: {
      ...c.header,
      transforms: [sortable],
    },
  }), columns);

export type Props = {
  query?: ?string,
  columns: any[],
  footer?: any[],
  rows: any[],
  onRowSelected?: Function,
  pagination?: bool,
  rowKey?: string | Function,
  onSort?: Function,
  sortColumnIndex?: string,
};

type SortingColumn = {
  direction: 'asc' | 'desc',
  position: number,
};

type SortingColumns = {
  [string]: SortingColumn,
};

type Pagination = {
  page: number,
  perPage: number,
  enabled: bool,
};

export type State = {
  sortingColumns: SortingColumns,
  pagination: Pagination,
};

type PaginatedResult = {
  amount: number,
  rows: any[],
};

class AdminList extends Component<Props, State> {
  static defaultProps = {
    rowKey: 'id',
  };

  sortable: any[];
  columns: any[];

  constructor(props: Props) {
    super(props);

    this.sortable = sort.sort({
      getSortingColumns: () => this.state.sortingColumns || [],
      onSort: selectedColumn => {
        const sortingColumns = sort.byColumn({
          sortingColumns: this.state.sortingColumns,
          sortingOrder,
          selectedColumn,
        });
        this.setState({
          sortingColumns,
        });
        this.onSort(sortingColumns);
      },
    });

    this.columns = mapColumns(this.sortable, props.columns);

    const columnKey = `${typeof props.sortColumnIndex !== 'undefined' ? props.sortColumnIndex : 0}`;
    this.state = {
      sortingColumns: {
        [columnKey]: {
          direction: 'asc',
          position: 0,
        },
      },
      pagination: {
        page: 1,
        perPage: 30,
        enabled: this.props.pagination || true,
      },
    };
  }

  componentDidMount() {
    this.onSort(this.state.sortingColumns);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (!_.isEqual(nextProps.columns, this.props.columns)) {
      this.columns = mapColumns(this.sortable, nextProps.columns);
    }
    if (!_.isEqual(nextProps.query, this.props.query)) {
      this.setState({
        pagination: {
          ...this.state.pagination,
          page: 1,
        },
      });
    }
  }

  onSort(sortingColumns) {
    const onSort = this.props.onSort;
    if (!!onSort) {
      onSort(rows => {
        const sorted = _.flow(
          resolve.resolve({
            columns: this.columns,
            method: resolve.nested,
          }),
          sort.sorter({columns: this.columns, sortingColumns, sort: orderBy})
        )(rows);
        return _.map(x => _.find(y => y.id === x.id, rows), sorted);
      });
    }
  }

  onPageSelect(page: number) {
    this.setState({pagination: {
      ...this.state.pagination,
      page,
    }});
  }

  onRowClick(row: any) {
    if (this.props.onRowSelected) {
      this.props.onRowSelected(row);
    }
  }

  paginateRows(pagination: Pagination): (rows: any[]) => PaginatedResult {
    if (!pagination.enabled) {
      return (rows: any[] = []) => ({
        amount: 1,
        rows,
      });
    }

    return (rows: any[] = []) => {
      const amountOfPages = Math.ceil(rows.length / pagination.perPage);
      const currentPage = pagination.page <= amountOfPages ? pagination.page : 1;
      const beginSlice = (currentPage - 1) * pagination.perPage;
      const endSlice = beginSlice + pagination.perPage;
      return {
        amount: amountOfPages,
        rows: rows.slice(beginSlice, endSlice),
      };
    };
  }

  searchRows(query: ?string, columns: any[]) {
    return (rows: any[] = []) => {
      const formattedRows = resolve.resolve({
        columns,
        method: resolve.nested,
      })(rows);

      if (!query) {
        return formattedRows;
      }

      const lowerQuery = _.toLower(query);
      return _.filter(
        r => _.some(
          c => _.includes(
            lowerQuery,
            _.toLower(_.get(c.property, r))
          ), columns
        ), formattedRows
      );
    };
  }

  render() {
    const {pagination, sortingColumns} = this.state;
    const {
      rows,
      query,
      footer,
      rowKey = 'id',
    } = this.props;
    const paginated = _.flow(
      this.searchRows(query, this.columns),
      sort.sorter({columns: this.columns, sortingColumns, sort: orderBy}),
      this.paginateRows(pagination)
    )(rows);

    return (
      <div className="AdminList">
        <Table.Provider
          className="table"
          columns={this.columns}>
          <Table.Header />
          <Table.Body
            rows={paginated.rows}
            onRow={row => ({onClick: () => this.onRowClick(row)})}
            rowKey={rowKey} />
          {footer &&  <tfoot>
            <tr>
              {mapWithIndex((item, index) => <td key={index}>{item}</td>, footer)}
            </tr>
          </tfoot> }
        </Table.Provider>
        {!paginated.rows.length &&
          <div className="callout secondary">
            Inga resultat hittades
          </div>
        }
        <div className="row align-center">
          <Paginator
            pagination={pagination}
            pages={paginated.amount}
            onSelect={page => this.onPageSelect(page)} />
        </div>
      </div>
    );
  }
}

export default AdminList;



// WEBPACK FOOTER //
// ./src/components/AdminList/AdminList.js